import { Injectable } from '@angular/core';

/**
 * Global boundary-aware positioning for table action menus.
 *
 * Tables have `overflow-x: auto` / `overflow-y: hidden` containers which clip
 * absolutely-positioned menus whenever a table has few rows (or whenever a menu opens near the edges).
 *
 * To fix this globally across all tables:
 * 1. Menus in tables are positioned with `position: fixed` relative to the viewport.
 * 2. They open ABOVE the table row by default (upwards), or below if there isn't enough space above.
 * 3. Being `position: fixed`, they escape all container overflow clipping and render cleanly
 *    on top of the table without ever being cut off.
 */
@Injectable({ providedIn: 'root' })
export class DropdownPositionService {
  private static readonly VIEWPORT_MARGIN = 8;
  private static readonly TABLE_DROPDOWN_SELECTOR =
    'table .dropdown, .table-container .dropdown, .table-scroll-container .dropdown, td.col-actions .dropdown, td .dropdown';

  private activeDropdown: HTMLElement | null = null;
  private activeMenu: HTMLElement | null = null;

  constructor() {
    document.addEventListener('focusin', this.handleTrigger, true);
    document.addEventListener('mouseover', this.handleTrigger, true);
    document.addEventListener('click', this.handleTrigger, true);

    window.addEventListener('scroll', this.handleScrollOrResize, { capture: true, passive: true });
    window.addEventListener('resize', this.handleScrollOrResize, { passive: true });
  }

  private handleTrigger = (event: Event): void => {
    const target = event.target as HTMLElement | null;
    const dropdown = target?.closest?.('.dropdown') as HTMLElement | null;
    if (!dropdown) return;

    // Check if this dropdown belongs to a table
    const isTableDropdown = !!dropdown.closest(DropdownPositionService.TABLE_DROPDOWN_SELECTOR);
    if (!isTableDropdown) return;

    const menu = dropdown.querySelector(':scope > .dropdown-menu') as HTMLElement | null;
    if (!menu) return;

    // If clicking an item inside the menu, let click proceed without repositioning
    if (target && menu.contains(target)) return;

    this.activeDropdown = dropdown;
    this.activeMenu = menu;

    this.positionTableMenu(dropdown, menu);
  };

  private positionTableMenu(dropdown: HTMLElement, menu: HTMLElement): void {
    const trigger = (dropdown.querySelector('label, button') || dropdown) as HTMLElement;
    const triggerRect = trigger.getBoundingClientRect();

    if (triggerRect.width === 0 && triggerRect.height === 0) return;

    const margin = DropdownPositionService.VIEWPORT_MARGIN;
    const menuWidth = menu.offsetWidth || 208;
    const menuHeight = menu.offsetHeight || 280;

    // Available space in viewport
    const spaceAbove = triggerRect.top - margin;
    const spaceBelow = window.innerHeight - triggerRect.bottom - margin;

    // Open downward if there is enough space below. Only flip upward if space below is insufficient and there is more room above.
    const openBelow = spaceBelow >= menuHeight || spaceBelow >= spaceAbove;

    menu.style.setProperty('position', 'fixed', 'important');
    menu.style.setProperty('z-index', '99999', 'important');
    menu.style.setProperty('overflow-y', 'auto', 'important');
    menu.style.setProperty('transform', 'none', 'important');
    menu.style.setProperty('margin', '0', 'important');

    // If an ancestor creates a containing block for fixed elements (e.g., via transform, filter, or contain),
    // coordinates must be calculated relative to that containing block rather than the viewport.
    const offsetParent = menu.offsetParent as HTMLElement | null;
    const hasContainingBlock = !!offsetParent && offsetParent !== document.body && offsetParent !== document.documentElement;
    const parentRect = hasContainingBlock ? offsetParent.getBoundingClientRect() : { left: 0, top: 0, right: window.innerWidth, bottom: window.innerHeight };

    if (openBelow) {
      // Anchored directly below the trigger
      const top = Math.round(triggerRect.bottom + 4 - parentRect.top);
      menu.style.setProperty('top', `${top}px`, 'important');
      menu.style.setProperty('bottom', 'auto', 'important');
      const maxH = Math.min(spaceBelow, window.innerHeight * 0.7);
      menu.style.setProperty('max-height', `${Math.round(maxH)}px`, 'important');
    } else {
      // Anchored directly above the trigger
      if (!hasContainingBlock) {
        menu.style.setProperty('top', 'auto', 'important');
        menu.style.setProperty('bottom', `${Math.round(window.innerHeight - triggerRect.top + 4)}px`, 'important');
      } else {
        const top = Math.round(triggerRect.top - menuHeight - 4 - parentRect.top);
        menu.style.setProperty('top', `${Math.max(margin, top)}px`, 'important');
        menu.style.setProperty('bottom', 'auto', 'important');
      }
      const maxH = Math.min(spaceAbove, window.innerHeight * 0.7);
      menu.style.setProperty('max-height', `${Math.round(maxH)}px`, 'important');
    }

    // Horizontal positioning: align with trigger left, avoid overflowing right edge of viewport
    const left = Math.round(triggerRect.left - parentRect.left);
    if (triggerRect.left + menuWidth > window.innerWidth - margin) {
      if (!hasContainingBlock) {
        menu.style.setProperty('left', 'auto', 'important');
        menu.style.setProperty('right', `${Math.max(margin, window.innerWidth - triggerRect.right)}px`, 'important');
      } else {
        const right = Math.round(parentRect.right - triggerRect.right);
        menu.style.setProperty('left', 'auto', 'important');
        menu.style.setProperty('right', `${Math.max(margin, right)}px`, 'important');
      }
    } else {
      menu.style.setProperty('left', `${Math.max(hasContainingBlock ? 0 : margin, left)}px`, 'important');
      menu.style.setProperty('right', 'auto', 'important');
    }
  }

  private handleScrollOrResize = (): void => {
    if (!this.activeDropdown || !this.activeMenu) return;

    const isFocused = this.activeDropdown.contains(document.activeElement);
    const isHovered = this.activeDropdown.matches(':hover');

    if (!isFocused && !isHovered) {
      this.activeDropdown = null;
      this.activeMenu = null;
      return;
    }

    this.positionTableMenu(this.activeDropdown, this.activeMenu);
  };
}
